import { create } from 'zustand';
import update from 'immutability-helper';
import { WIDGET_TYPE_MAP } from '../config';
import { buildQueryConfig, buildWidgetConfig, CONCURRENT_QUERY_LIMIT } from './utils';
import { isEmpty } from 'lodash';
import { filterSupportedWidgets, getDefaultDashboardConfig } from '../config/utils';

export const useDashboardStore = create((set) => ({
  name: 'widget-store',
  widgets: {},
  order: [],
  queryManager: buildQueryConfig(),
}));

export const initWidgetStore = (persistedState) => {
  let newState = persistedState;
  if (!persistedState || isEmpty(persistedState.widgets)) {
    newState = getDefaultDashboardConfig();
  }

  // filter state
  const widgets = filterSupportedWidgets(newState.widgets);

  return useDashboardStore.setState((state) => ({
    ...state,
    ...newState,
    widgets,
    // query manager is setup once on init, queue is built as part of it
    queryManager: buildQueryConfig(),
  }));
};

// used to add a new widget
export const initWidget = ({ type, state }) => {
  const widgetState = buildWidgetConfig({ type, state });

  useDashboardStore.setState((state) => {
    return update(state, {
      widgets: {
        $merge: {
          [widgetState.wid]: widgetState,
        },
      },
      order: { $unshift: [widgetState.wid] },
    });
  });
  return widgetState.wid;
};

export const updateWidget = (wid, change) =>
  useDashboardStore.setState((state) => {
    return update(state, {
      widgets: {
        [wid]: {
          state: {
            $merge: change,
          },
        },
      },
    });
  });

export const removeWidget = (wid) =>
  useDashboardStore.setState((state) => {
    return update(state, {
      widgets: {
        $unset: [wid],
      },
    });
  });

export const updateOrder = (newOrder) =>
  useDashboardStore.setState((state) => {
    return update(state, {
      order: {
        $set: newOrder,
      },
    });
  });

// for persistence (no subscription but saga will pull on trigger)
export const getWidgetStateForPersistence = () => {
  const { widgets, order }: any = useDashboardStore.getState();
  return { widgets, order };
};

// for save/share (subscription from latch hooks)
export const useWidgetStateForSave = () => {
  const { widgets, order }: any = useDashboardStore((state) => state);
  return { widgets, order };
};

export function useWidget({ wid, initialState = {} }) {
  const update = (newState) => {
    updateWidget(wid, newState);
  };

  const remove = () => {
    removeWidget(wid);
  };

  const widgetStore = useDashboardStore((state) => state.widgets[wid]);

  // meta data is constant and shouldn't be part of state
  const { meta } = WIDGET_TYPE_MAP[widgetStore.type];

  return {
    state: widgetStore.state || {},
    update,
    remove,
    meta,
  };
}

export function getAddedWidgetsLength() {
  const { widgets }: any = useDashboardStore.getState();
  return Object.keys(widgets).length;
}
